import { Description } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/Done';
import ImageSearchOutlined from '@mui/icons-material/ImageSearch';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { capitalize, ImageLoader } from '@prismamedia/one-components';
import classNames from 'classnames';
import { format } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { FC, useEffect, useState } from 'react';
import { PUBLISHED_AT_TEXT, UNKNOWN_TEXT } from '../constants';
import { useStyles as sharedUseStyles } from '../styles';
import { ListItemCardProps } from '../types';
import { ActionsMenu } from './ActionsMenu';
import { useStyles } from './styles';

export const ListItemCard: FC<ListItemCardProps> = ({
  chip,
  image,
  isBusy = false,
  isDisabled = false,
  isSelected = false,
  onItemClick,
  publicUrl,
  publishDate,
  site,
  status,
  text,
  title,
  isSuggestionItem,
  slideShowArticleCount,
}) => {
  const [isInnerBusy, setIsInnerBusy] = useState<boolean>(isBusy);
  const classes = useStyles({
    isDisabled: isDisabled || isBusy,
    isSelected,
    title,
  });
  const sharedClasses = sharedUseStyles({ isDisabled: isDisabled || isBusy });

  const labelizedPublishedDate = publishDate
    ? format(new Date(publishDate), 'dd/MM/yyyy à HH:mm', { locale: fr })
    : '';

  useEffect(() => {
    !isDisabled && setIsInnerBusy(false);
  }, [isDisabled]);

  useEffect(() => {
    isSelected && setIsInnerBusy(isBusy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusy]);

  return (
    <Card
      data-testid="search-item"
      className={classNames(
        classes.wrapperCard,
        isSelected && sharedClasses.selectedWrapperCard,
        isSuggestionItem && sharedClasses.shiningCard,
      )}
      onClick={() => {
        if (image && !isSelected) {
          setIsInnerBusy(true);
        }
        onItemClick();
      }}
      variant="outlined"
    >
      {/* Icon check */}
      {isInnerBusy ? (
        <ListItemIcon className={classes.busyIcon}>
          <CircularProgress size={20} />
        </ListItemIcon>
      ) : (
        isSelected && (
          <ListItemIcon className={classes.selectedIcon}>
            <CheckCircleIcon fontSize="small" />
          </ListItemIcon>
        )
      )}

      {/* Media */}
      <div className={classes.wrapperMedia}>
        {chip && <Typography className={classes.brandkey}>{chip}</Typography>}
        <ImageLoader
          className={classes.media}
          image={
            <ImageSearchOutlined className={sharedClasses.mediaFallback} />
          }
          src={image}
        />
      </div>

      <CardContent className={classes.wrapperContent}>
        {/* Header */}
        <div className={classes.wrapperHeader}>
          <Typography variant="body2">
            {capitalize(text || UNKNOWN_TEXT)}{' '}
            {publishDate &&
              ` - ${status || PUBLISHED_AT_TEXT} ${labelizedPublishedDate}.`}
          </Typography>
        </div>

        {/* Content */}
        <div>
          {title && (
            <Typography
              className={classes.title}
              gutterBottom
              variant="subtitle1"
            >
              {capitalize(title || '--')}
            </Typography>
          )}

          {publicUrl && (
            <CardActions className={classes.actionsMenu}>
              <ActionsMenu publicUrl={publicUrl} />
            </CardActions>
          )}
        </div>

        {/* Footer */}
        <Box
          sx={{
            alignItems: 'flex-end',
            display: 'flex',
            flex: '1 auto',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              typography: 'caption',
              color: isDisabled ? grey['500'] : 'primary.main',
            }}
          >
            {site}
          </Box>
          {slideShowArticleCount ? (
            <Box
              sx={{
                typography: 'caption',
                alignItems: 'flex-end',
                display: 'flex',
                color: grey['500'],
              }}
            >
              <Box>{slideShowArticleCount}</Box>
              <Box
                sx={{
                  alignItems: 'flex-end',
                  display: 'flex',
                }}
              >
                <Description sx={{ pt: '2px' }} />
              </Box>
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};
